import { useCallback, useEffect, useState } from "react";
import { AdService } from "@services/ads/AdService";
import { CommentService } from "@services/comment/CommentService";
import { appStore } from "@/store/appStore";
import type { Ad } from "@models/Ad.model";

interface ReviewCommentListProps {
  rootAd?: Ad | null;
  /** Incremented externally to force a refetch after a post. */
  refreshKey?: number;
  /**
   * Show the composer (MPN-1454). Off by default: this list is also rendered on the
   * client-facing review panel, which has its own input and its own posting rules.
   */
  canComment?: boolean;
  /** Number of comments currently on the ad, reported after each load (MPN-1626). */
  onCountChange?: (count: number) => void;
}

/*
    Comments on a GuidePost.

    Ops could read these and not write them (MPN-1454): commenting was gated on three
    engagement roles, all client-side, so internal feedback on content that needed reworking
    had nowhere to go. The composer below is that gap.

    Each comment carries whether the client sees it. An internal comment starts hidden --
    the point is somewhere to say "this needs work" BEFORE the client is shown it -- and the
    author can share it afterwards. The badge is always rendered for a hidden comment rather
    than only on hover, because the one thing worth never getting wrong is someone believing
    a note is private when it is not.
*/
export default function ReviewCommentList({ rootAd, refreshKey, canComment, onCountChange }: ReviewCommentListProps) {
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState("");
  const [shareWithClient, setShareWithClient] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [localKey, setLocalKey] = useState(0);

  useEffect(() => {
    if (!rootAd) {
      setComments([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    new AdService()
      .getAdComments(rootAd.id)
      .then((result) => {
        if (cancelled) return;
        setComments(result);
        // So the review panel can tell "rejected with no explanation" from "rejected
        // after saying why" (MPN-1626). Reported from here because this is the only
        // place that knows what is actually on the ad.
        onCountChange?.(result.length);
      })
      .catch(() => { if (!cancelled) setError("Failed to load comments"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rootAd?.id, refreshKey, localKey]);

  const submit = useCallback(async () => {
    const body = draft.trim();
    const orgId = appStore.loggedInUser?.organization?.id;
    if (!body || !rootAd || posting || !orgId) return;
    setPosting(true);
    setPostError(null);
    try {
      await new CommentService().postAdComment(orgId, rootAd.id, body, shareWithClient);
      setDraft("");
      // Back to internal after each send. The visible state is the one that cannot be
      // undone, so it should never be inherited from the last comment by accident.
      setShareWithClient(false);
      setLocalKey((k) => k + 1);
    } catch (e) {
      // The server explains these ("this ad is not in review"), and httpRequest already
      // carries that text through. Swallowing it left Rachel with "Could not post
      // comment" and nothing to act on (MPN-1631). Only trust the message when it came
      // from a response -- a dropped connection rejects with "Failed to fetch", which
      // is worse than the generic line.
      const err = e as Error & { status?: number };
      setPostError(err?.status && err.message ? err.message : "Could not post comment");
    } finally {
      setPosting(false);
    }
  }, [draft, rootAd, posting, shareWithClient]);

  return (
    <div className="ceh-review-comment-list">
      <div className="ceh-review-comment-list-header">Review Comments</div>

      {loading && <div className="ceh-review-comment-list-empty">Loading...</div>}
      {error && <div className="ceh-review-comment-list-empty">{error}</div>}
      {!loading && !error && comments.length === 0 && (
        <div className="ceh-review-comment-list-empty">No review comments yet</div>
      )}

      {!loading && !error && comments.length > 0 && (
        <div className="ceh-review-comment-list-comments">
          {comments.map((comment: any) => (
            <div key={comment.id} className="ceh-review-comment">
              <div className="ceh-review-comment-meta">
                <span className="ceh-review-comment-author">
                  {comment.user?.employee?.person?.first_name ?? ""}{" "}
                  {comment.user?.employee?.person?.last_name ?? ""}
                </span>
                <span className="ceh-review-comment-date">
                  {new Date(comment.create_date).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              {comment.is_client_visible === false && (
                <span
                  className="ceh-review-comment-internal"
                  title="Only Motimatic can see this comment. The client's review panel does not show it."
                >
                  Internal only
                </span>
              )}
              <div className="ceh-review-comment-body">{comment.body}</div>
            </div>
          ))}
        </div>
      )}

      {canComment && rootAd && (
        <div className="ceh-review-comment-compose">
          <textarea
            className="ceh-review-comment-compose__input"
            placeholder="Add a comment…"
            aria-label="Add a comment"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={posting}
            rows={3}
          />
          <label className="ceh-review-comment-compose__share">
            <input
              type="checkbox"
              checked={shareWithClient}
              onChange={(e) => setShareWithClient(e.target.checked)}
              disabled={posting}
            />
            Visible to client
          </label>
          {postError && (
            <div className="ceh-review-comment-compose__error">{postError}</div>
          )}
          <button
            type="button"
            className="ceh-review-comment-compose__send"
            onClick={submit}
            disabled={posting || draft.trim().length === 0}
          >
            {posting ? "Posting…" : "Comment"}
          </button>
        </div>
      )}
    </div>
  );
}
